import { selectPropertyFromLocationHash } from "components/navigation/URL";
import { type Company, useGetCompanyListCompanyGet } from "kubb";
import { isUserSuperAdmin } from "model/user";
import { useLocation } from "react-router-dom";
import { useProfile } from "./useProfile";

// The global company selector fetches the whole list once and filters
// client-side. See CompanyListGate for the TODO on paginating past this ceiling.
export const COMPANY_LIST_PARAMS = { max_items: 1000 } as const;
const COMPANY_LIST_STALE_TIME = 5 * 60 * 1000;

/**
 * The single shared company-list query. Every consumer hits the same cache key
 * (derived from COMPANY_LIST_PARAMS), so after CompanyListGate warms it this is
 * just a cache read. TanStack owns the data, invalidation and refetch.
 */
export function useCompanyListQuery() {
  return useGetCompanyListCompanyGet(COMPANY_LIST_PARAMS, {
    query: { staleTime: COMPANY_LIST_STALE_TIME },
  });
}

export function useCompanies(): Array<Company> | undefined {
  return useCompanyListQuery().data?.data;
}

/**
 * The selected company id, derived from the URL hash (the source of truth for
 * selection). Returns one of:
 *   - "<id>" / "ALL" / "NONE" when present in the hash
 *   - the first company for a non-superadmin with no company in the hash
 *   - "NONE" otherwise (e.g. a superadmin who has not picked a company yet)
 */
export function useSelectedCompanyId(): string {
  const location = useLocation();
  const profile = useProfile();
  const companies = useCompanies();

  const fromHash = selectPropertyFromLocationHash("company", location.hash);
  if (fromHash === "ALL" || fromHash === "NONE") {
    return fromHash;
  }
  // Only honour a hash company id that is actually in the loaded list — a stale
  // or cross-account id would otherwise leak into API calls as a bogus filter.
  if (fromHash && companies?.some((c) => c.company_id === fromHash)) {
    return fromHash;
  }
  if (!isUserSuperAdmin(profile) && companies && companies.length >= 1) {
    return companies[0].company_id;
  }
  return "NONE";
}

export function useSelectedCompany(): Company | undefined {
  const id = useSelectedCompanyId();
  const companies = useCompanies();
  if (id === "NONE" || id === "ALL") {
    return undefined;
  }
  return companies?.find((company) => company.company_id === id);
}

// Like useSelectedCompany but keeps the "ALL" sentinel, for the few consumers
// (the company selector + list) that distinguish "all companies" from "none".
export function useSelectedCompanyOrAll(): Company | "ALL" | undefined {
  const id = useSelectedCompanyId();
  const company = useSelectedCompany();
  return id === "ALL" ? "ALL" : company;
}

export function useRequiredSelectedCompany(): Company {
  const company = useSelectedCompany();
  if (!company) {
    throw new Error("Company not found");
  }
  return company;
}

export function useRequiredSelectedCompanyId(): string {
  const id = useSelectedCompanyId();
  if (id === "NONE") {
    throw new Error("Company not found");
  }
  return id;
}
